/**
 * Payments List Page (Unified)
 *
 * Consolidated payment management including:
 * - Bank transactions with edit/allocation functionality
 * - Potential matches for unreconciled transactions
 * - Credits management
 * - Unmatched Xero invoices
 * - Xero connection status
 */

import React, { useState } from 'react';
import {
  Card,
  Table,
  Space,
  Typography,
  Tag,
  Button,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Tooltip,
  Statistic,
  Tabs,
  Modal,
  message,
  Divider,
  Badge,
  Progress,
  Alert,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  ReloadOutlined,
  LinkOutlined,
  SearchOutlined,
  FilterOutlined,
  DollarOutlined,
  PlusOutlined,
  EditOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  BankOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { bankTransactionsApi, reconciliationApi } from '../services/api';
import { colors, spacing } from '../components/design-system/tokens';
import dayjs from 'dayjs';

import BankTransactionAllocation from '../components/reconciliation/BankTransactionAllocation';
import XeroConnectionStatus from '../components/reconciliation/XeroConnectionStatus';
import CreditsTab from '../components/reconciliation/CreditsTab';
import UnmatchedInvoicesTab from '../components/reconciliation/UnmatchedInvoicesTab';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

type TransactionType = 'WIRE_IN' | 'WIRE_OUT' | 'CHECK_IN' | 'CHECK_OUT' | 'ACH_IN' | 'ACH_OUT';
type TransactionStatus = 'UNRECONCILED' | 'PARTIAL' | 'RECONCILED' | 'PUSHED';

interface BankTransaction {
  id: string;
  transactionType: TransactionType;
  amount: number;
  date: string;
  reference: string;
  counterparty: string;
  description: string;
  status: TransactionStatus;
  allocatedAmount?: number;
  allocations?: Array<{
    id: string;
    shipmentId: string;
    amount: number;
    shipment?: {
      id: string;
      reference: string;
      contract?: {
        reference: string;
      };
    };
  }>;
}

interface ShipmentCandidate {
  id: string;
  reference: string;
  contractRef: string;
  buyerName: string;
  supplierName: string;
  buyerPrice: number | null;
  supplierPrice: number | null;
  estimatedValue?: number;
  eta: string | null;
  status: string;
  commissionWithheldFromPayment?: boolean;
  commissionRate?: number | null;
}

interface ScoreBreakdown {
  text: number;
  amount: number;
  date: number;
  history: number;
  total: number;
}

interface MatchResult {
  shipment: ShipmentCandidate;
  confidence: number;
  scores: ScoreBreakdown;
  reasoning: string[];
  isMultiShipment: boolean;
  combinedShipments?: ShipmentCandidate[];
}

const statusColors = {
  success: '#169959',
  error: '#C32928',
  warning: '#E6B339',
  info: '#179DB5',
};

const textColors = {
  secondary: '#8C8C8C',
  primary: '#262626',
};

const typeLabels: Record<TransactionType, string> = {
  WIRE_IN: 'Wire',
  WIRE_OUT: 'Wire',
  CHECK_IN: 'Check',
  CHECK_OUT: 'Check',
  ACH_IN: 'ACH',
  ACH_OUT: 'ACH',
};

const typeColors: Record<TransactionType, string> = {
  WIRE_IN: 'green',
  WIRE_OUT: 'red',
  CHECK_IN: 'cyan',
  CHECK_OUT: 'orange',
  ACH_IN: 'blue',
  ACH_OUT: 'purple',
};

const statusConfig: Record<TransactionStatus, { color: string; label: string }> = {
  UNRECONCILED: { color: 'default', label: 'Unreconciled' },
  PARTIAL: { color: 'warning', label: 'Partial' },
  RECONCILED: { color: 'processing', label: 'Reconciled' },
  PUSHED: { color: 'success', label: 'Pushed to Xero' },
};

const ConfidenceBar: React.FC<{ confidence: number }> = ({ confidence }) => {
  const percent = Math.round(confidence * 100);
  let color = statusColors.error;
  if (percent >= 80) color = statusColors.success;
  else if (percent >= 60) color = statusColors.warning;

  return (
    <Tooltip title={`${percent}% confidence`}>
      <Progress
        percent={percent}
        size="small"
        strokeColor={color}
        showInfo={false}
        style={{ width: 60 }}
      />
    </Tooltip>
  );
};

const ScoreBreakdownDisplay: React.FC<{ scores: ScoreBreakdown }> = ({ scores }) => (
  <div style={{ fontSize: 12 }}>
    <Row gutter={[8, 4]}>
      <Col span={12}>
        <span style={{ color: textColors.secondary }}>Text: </span>
        <span>{Math.round(scores.text * 100)}%</span>
      </Col>
      <Col span={12}>
        <span style={{ color: textColors.secondary }}>Amount: </span>
        <span>{Math.round(scores.amount * 100)}%</span>
      </Col>
      <Col span={12}>
        <span style={{ color: textColors.secondary }}>Date: </span>
        <span>{Math.round(scores.date * 100)}%</span>
      </Col>
      <Col span={12}>
        <span style={{ color: textColors.secondary }}>History: </span>
        <span>{Math.round(scores.history * 100)}%</span>
      </Col>
    </Row>
  </div>
);

const PotentialMatchesModal: React.FC<{
  visible: boolean;
  transaction: BankTransaction | null;
  onClose: () => void;
  onConfirm: (match: MatchResult) => void;
}> = ({ visible, transaction, onClose, onConfirm }) => {
  const [selectedMatch, setSelectedMatch] = useState<MatchResult | null>(null);

  const { data: matchesData, isLoading } = useQuery({
    queryKey: ['potential-matches', transaction?.id],
    queryFn: async () => {
      if (!transaction) return { matches: [] };
      
      const allText = [
        transaction.reference || '',
        transaction.counterparty || '',
        transaction.description || '',
      ].join(' ');
      
      const extractedRefs: string[] = [];
      
      // Pattern 1: Standard CR-XX#####.## format
      const standardPattern = /CR-[A-Z]{2}\d{5}(?:\.\d{2})?/gi;
      let match;
      while ((match = standardPattern.exec(allText)) !== null) {
        extractedRefs.push(match[0].toUpperCase());
      }
      
      // Pattern 2: Invoice format with range - e.g., SANBR24127.01-5 → CR-BR24127.01 through .05
      const rangePattern = /[A-Z]{3}([A-Z]{2}\d{5})\.(\d+)-(\d+)/gi;
      while ((match = rangePattern.exec(allText)) !== null) {
        if (match[1] && match[2] && match[3]) {
          const base = match[1].toUpperCase();
          const start = parseInt(match[2], 10);
          const end = parseInt(match[3], 10);
          const padLength = match[2].length;
          for (let i = start; i <= end; i++) {
            const ref = `CR-${base}.${i.toString().padStart(padLength, '0')}`;
            if (!extractedRefs.includes(ref)) {
              extractedRefs.push(ref);
            }
          }
        }
      }

      // Pattern 3: Invoice format - 3-letter buyer code + 2-letter supplier code + 5 digits
      // e.g., NWCCA25477 → CR-CA25477, SKKCA25459 → CR-CA25459
      const invoicePattern = /[A-Z]{3}([A-Z]{2}\d{5}(?:\.\d{2})?)/gi;
      while ((match = invoicePattern.exec(allText)) !== null) {
        if (match[1]) {
          const ref = `CR-${match[1].toUpperCase()}`;
          if (!extractedRefs.includes(ref)) {
            extractedRefs.push(ref);
          }
        }
      }
      
      // Pattern 4: Standalone 2-letter + 5-digit format (e.g., CA25477)
      const standalonePattern = /\b([A-Z]{2}\d{5}(?:\.\d{2})?)\b/gi;
      while ((match = standalonePattern.exec(allText)) !== null) {
        if (match[1]) {
          const ref = `CR-${match[1].toUpperCase()}`;
          if (!extractedRefs.includes(ref)) {
            extractedRefs.push(ref);
          }
        }
      }
      
      const wireFormat = {
        source: 'pdf' as const,
        sourceFile: 'bank-transaction',
        direction: (transaction.transactionType.endsWith('_IN') ? 'incoming' : 'outgoing') as 'incoming' | 'outgoing',
        amount: Number(transaction.amount),
        date: new Date(transaction.date),
        trn: transaction.reference || undefined,
        payer: transaction.counterparty || undefined,
        paymentDetail: transaction.description || undefined,
        contractRefs: extractedRefs,
        description: transaction.description || '',
      };
      
      const response = await reconciliationApi.matchTransaction(wireFormat);
      return response as unknown as { matches: MatchResult[] };
    },
    enabled: visible && !!transaction,
    staleTime: 30000,
  });

  const matches = matchesData?.matches || [];

  if (!transaction) return null;

  const handleConfirm = () => {
    if (selectedMatch) {
      onConfirm(selectedMatch);
      onClose();
    }
  };

  return (
    <Modal
      title={
        <Space>
          <LinkOutlined />
          <span>Potential Matches</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="edit"
          onClick={() => {
            onClose();
          }}
        >
          Edit Transaction
        </Button>,
        <Button
          key="confirm"
          type="primary"
          onClick={handleConfirm}
          disabled={!selectedMatch}
        >
          Apply Match
        </Button>,
      ]}
    >
      {/* Transaction Summary */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <Statistic
              title="Amount"
              value={Number(transaction.amount)}
              prefix={transaction.transactionType.endsWith('_IN') ? '+$' : '-$'}
              precision={2}
              valueStyle={{
                color: transaction.transactionType.endsWith('_IN')
                  ? statusColors.success
                  : statusColors.error,
              }}
            />
          </Col>
          <Col span={8}>
            <Statistic title="Date" value={dayjs(transaction.date).format('MMM DD, YYYY')} />
          </Col>
          <Col span={8}>
            <div style={{ color: textColors.secondary, fontSize: 12 }}>Reference</div>
            <div style={{ fontWeight: 500, fontFamily: 'monospace' }}>{transaction.reference || 'N/A'}</div>
          </Col>
        </Row>
        <Divider style={{ margin: '12px 0' }} />
        <Row gutter={16}>
          <Col span={12}>
            <div style={{ color: textColors.secondary, fontSize: 12 }}>Counterparty</div>
            <div style={{ fontWeight: 500 }}>{transaction.counterparty || 'Unknown'}</div>
          </Col>
          <Col span={12}>
            <div style={{ color: textColors.secondary, fontSize: 12 }}>Description</div>
            <div style={{ fontSize: 12 }}>{transaction.description || 'N/A'}</div>
          </Col>
        </Row>
      </Card>

      <div>
        <Title level={5} style={{ margin: '0 0 8px 0' }}>
          Suggested Matches
        </Title>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Loading matches...</div>
        ) : matches.length === 0 ? (
          <Alert
            message="No matches found"
            description="Try editing the transaction to add allocations manually"
            type="warning"
            showIcon
          />
        ) : (
          <div style={{ maxHeight: 350, overflowY: 'auto' }}>
            {matches.map((match: MatchResult, index: number) => (
              <Card
                key={match.shipment.id}
                size="small"
                hoverable
                onClick={() => setSelectedMatch(match)}
                style={{
                  marginBottom: 8,
                  border:
                    selectedMatch?.shipment.id === match.shipment.id
                      ? '2px solid #1890ff'
                      : undefined,
                  cursor: 'pointer',
                }}
              >
                <Row gutter={16} align="middle">
                  <Col span={1}>
                    <Badge
                      count={index + 1}
                      style={{
                        backgroundColor:
                          match.confidence >= 0.8
                            ? statusColors.success
                            : match.confidence >= 0.6
                              ? statusColors.warning
                              : statusColors.error,
                      }}
                    />
                  </Col>
                  <Col span={5}>
                    <div style={{ fontWeight: 600 }}>
                      {match.isMultiShipment
                        ? `${match.combinedShipments?.length} Shipments`
                        : match.shipment.reference}
                    </div>
                    {match.isMultiShipment && (
                      <div style={{ fontSize: 11, color: textColors.secondary }}>
                        {match.combinedShipments?.map((s) => s.reference).join(', ')}
                      </div>
                    )}
                  </Col>
                  <Col span={4}>
                    <ConfidenceBar confidence={match.confidence} />
                    <div style={{ fontSize: 11 }}>{Math.round(match.confidence * 100)}%</div>
                  </Col>
                  <Col span={6}>
                    <div style={{ fontSize: 12 }}>
                      {match.isMultiShipment
                        ? match.combinedShipments?.[0]?.buyerName
                        : match.shipment.buyerName}
                    </div>
                    <div style={{ fontSize: 11, color: textColors.secondary }}>
                      $
                      {(match.isMultiShipment
                        ? match.combinedShipments?.reduce(
                            (s, sh) => s + (sh.buyerPrice || sh.supplierPrice || 0),
                            0
                          )
                        : match.shipment.buyerPrice || match.shipment.supplierPrice || 0
                      )?.toLocaleString()}
                    </div>
                  </Col>
                  <Col span={8}>
                    <div style={{ fontSize: 11, color: textColors.secondary }}>
                      {match.reasoning.slice(0, 2).join(' • ')}
                    </div>
                  </Col>
                </Row>
                {selectedMatch?.shipment.id === match.shipment.id && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #f0f0f0' }}>
                    <ScoreBreakdownDisplay scores={match.scores} />
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

const BankTransactionsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [typeFilter, setTypeFilter] = useState<TransactionType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<TransactionStatus | 'all'>('all');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [searchText, setSearchText] = useState('');
  const [directionFilter, setDirectionFilter] = useState<'all' | 'IN' | 'OUT'>('all');

  const [showAllocationForm, setShowAllocationForm] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);

  const [showMatchesModal, setShowMatchesModal] = useState(false);
  const [matchingTransaction, setMatchingTransaction] = useState<BankTransaction | null>(null);
  const [selectedMatch, setSelectedMatch] = useState<MatchResult | null>(null);
  const [initialTransactionData, setInitialTransactionData] = useState<{
    transactionType?: string;
    amount?: number;
    date?: string;
    reference?: string;
    counterparty?: string;
    description?: string;
  } | null>(null);

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ['bank-transactions-summary'],
    queryFn: async () => {
      const result = await bankTransactionsApi.getSummary();
      return result as any;
    },
    staleTime: 30000,
  });

  const { data: transactionsData, isLoading, refetch } = useQuery({
    queryKey: ['bank-transactions', typeFilter, statusFilter, dateRange, directionFilter],
    queryFn: async () => {
      const params: any = { limit: 200 };

      if (typeFilter !== 'all') {
        params.transactionType = typeFilter;
      }

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (dateRange) {
        params.startDate = dateRange[0].format('YYYY-MM-DD');
        params.endDate = dateRange[1].format('YYYY-MM-DD');
      }

      return bankTransactionsApi.list(params);
    },
  });

  const rawData = (transactionsData as any)?.data || transactionsData;
  let transactions: BankTransaction[] = rawData?.transactions || [];

  if (directionFilter !== 'all') {
    transactions = transactions.filter((t) => t.transactionType.endsWith(`_${directionFilter}`));
  }

  if (searchText) {
    const search = searchText.toLowerCase();
    transactions = transactions.filter(
      (t) =>
        t.reference?.toLowerCase().includes(search) ||
        t.counterparty?.toLowerCase().includes(search) ||
        t.description?.toLowerCase().includes(search) ||
        String(t.amount).includes(search)
    );
  }

  const handleEdit = (transaction: BankTransaction) => {
    setSelectedTransactionId(transaction.id);
    setShowAllocationForm(true);
  };

  const handleShowMatches = (transaction: BankTransaction) => {
    setMatchingTransaction(transaction);
    setShowMatchesModal(true);
  };

  const handleMatchConfirm = (match: MatchResult) => {
    message.success(`Match selected: ${match.shipment.reference}`);
    if (matchingTransaction) {
      setSelectedMatch(match);
      setInitialTransactionData({
        transactionType: matchingTransaction.transactionType,
        amount: Number(matchingTransaction.amount),
        date: matchingTransaction.date,
        reference: matchingTransaction.reference || undefined,
        counterparty: matchingTransaction.counterparty || undefined,
        description: matchingTransaction.description || undefined,
      });
      setSelectedTransactionId(matchingTransaction.id);
      setShowAllocationForm(true);
    }
  };

  const handleFormSaved = () => {
    setShowAllocationForm(false);
    setSelectedTransactionId(null);
    setSelectedMatch(null);
    setInitialTransactionData(null);
    queryClient.invalidateQueries({ queryKey: ['bank-transactions'] });
    queryClient.invalidateQueries({ queryKey: ['bank-transactions-summary'] });
  };

  const totals = transactions.reduce(
    (acc, t) => {
      const isIncoming = t.transactionType.endsWith('_IN');
      if (isIncoming) {
        acc.incoming += Number(t.amount);
        acc.inCount++;
      } else {
        acc.outgoing += Number(t.amount);
        acc.outCount++;
      }
      return acc;
    },
    { incoming: 0, outgoing: 0, inCount: 0, outCount: 0 }
  );

  const columns: ColumnsType<BankTransaction> = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 110,
      render: (date: string) => dayjs(date).format('MMM D, YYYY'),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Type',
      dataIndex: 'transactionType',
      key: 'type',
      width: 100,
      render: (type: TransactionType) => (
        <Tag
          color={typeColors[type]}
          icon={type.endsWith('_IN') ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
        >
          {typeLabels[type]}
        </Tag>
      ),
    },

    {
      title: 'Counterparty',
      dataIndex: 'counterparty',
      key: 'counterparty',
      width: 180,
      ellipsis: true,
      render: (party: string) => party || <Text type="secondary">-</Text>,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 130,
      align: 'right',
      render: (amount: number, record) => {
        const isIncoming = record.transactionType.endsWith('_IN');
        const formatted = Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        // Fixed-width format: positive numbers pad to 13 chars, negative (with parens) pad to 14
        const displayValue = isIncoming 
          ? `$${formatted.padStart(13, ' ')}`
          : `$${`(${formatted})`.padStart(14, ' ')}`;
        return (
          <span
            style={{
              color: isIncoming ? 'inherit' : colors.status.negative,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              fontFamily: 'monospace',
              whiteSpace: 'pre',
            }}
          >
            {displayValue}
          </span>
        );
      },
      sorter: (a, b) => Number(a.amount) - Number(b.amount),
    },
    {
      title: 'Allocations',
      key: 'allocations',
      width: 100,
      render: (_, record) => {
        const count = record.allocations?.length || 0;
        const allocated = record.allocatedAmount || 0;
        const total = Number(record.amount);
        const pct = total > 0 ? Math.round((allocated / total) * 100) : 0;
        return (
          <Tooltip title={`${count} allocation(s), ${pct}% allocated`}>
            <Tag color={pct === 100 ? 'green' : pct > 0 ? 'orange' : 'default'}>
              {count} ({pct}%)
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: TransactionStatus) => {
        const config = statusConfig[status] || { color: 'default', label: status };
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: 'Notes',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc: string) =>
        desc ? <Text type="secondary">{desc}</Text> : <Text type="secondary">-</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleEdit(record)}
          >
            View
          </Button>
          {record.status === 'UNRECONCILED' && (
            <Tooltip title="Find potential matches">
              <Button
                size="small"
                icon={<SearchOutlined />}
                onClick={() => handleShowMatches(record)}
              >
                Match
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  if (showAllocationForm) {
    return (
      <div>
        <Button
          onClick={() => {
            setShowAllocationForm(false);
            setSelectedTransactionId(null);
            handleFormSaved();
          }}
          style={{ marginBottom: 16 }}
        >
          ← Back to List
        </Button>
        <BankTransactionAllocation
          transactionId={selectedTransactionId || undefined}
          initialData={initialTransactionData || undefined}
          initialShipments={selectedMatch ? (
            selectedMatch.isMultiShipment && selectedMatch.combinedShipments
              ? selectedMatch.combinedShipments.map((s) => ({
                  shipmentId: s.id,
                  shipmentRef: s.reference,
                  contractRef: s.contractRef,
                  buyerName: s.buyerName,
                  supplierName: s.supplierName,
                  estimatedValue: s.estimatedValue || s.buyerPrice || s.supplierPrice || 0,
                  commissionWithheldFromPayment: s.commissionWithheldFromPayment,
                  commissionRate: s.commissionRate,
                }))
              : [{
                  shipmentId: selectedMatch.shipment.id,
                  shipmentRef: selectedMatch.shipment.reference,
                  contractRef: selectedMatch.shipment.contractRef,
                  buyerName: selectedMatch.shipment.buyerName,
                  supplierName: selectedMatch.shipment.supplierName,
                  estimatedValue: selectedMatch.shipment.estimatedValue || selectedMatch.shipment.buyerPrice || selectedMatch.shipment.supplierPrice || 0,
                  commissionWithheldFromPayment: selectedMatch.shipment.commissionWithheldFromPayment,
                  commissionRate: selectedMatch.shipment.commissionRate,
                }]
          ) : undefined}
          onSaved={handleFormSaved}
        />
      </div>
    );
  }

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: spacing.lg }}>
        <Col span={5}>
          <Card loading={summaryLoading}>
            <Statistic
              title="Unreconciled"
              value={summary?.unreconciled?.count || 0}
              prefix={<ClockCircleOutlined style={{ color: textColors.secondary }} />}
              suffix={
                <span style={{ fontSize: 12, color: textColors.secondary }}>
                  ${(summary?.unreconciled?.amount || 0).toLocaleString()}
                </span>
              }
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card loading={summaryLoading}>
            <Statistic
              title="Partial"
              value={summary?.partial?.count || 0}
              prefix={<ClockCircleOutlined style={{ color: statusColors.warning }} />}
              valueStyle={{ color: statusColors.warning }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card loading={summaryLoading}>
            <Statistic
              title="Reconciled"
              value={summary?.reconciled?.count || 0}
              prefix={<CheckCircleOutlined style={{ color: statusColors.info }} />}
              valueStyle={{ color: statusColors.info }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card loading={summaryLoading}>
            <Statistic
              title="Pushed to Xero"
              value={summary?.pushed?.count || 0}
              prefix={<CheckCircleOutlined style={{ color: statusColors.success }} />}
              valueStyle={{ color: statusColors.success }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setSelectedTransactionId(null);
                setShowAllocationForm(true);
              }}
              block
              style={{ height: '100%', minHeight: 60 }}
            >
              New Transaction
            </Button>
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: spacing.md }}>
        <Space style={{ marginBottom: 0 }} wrap>
          <Input
            placeholder="Search reference, party, description..."
            prefix={<SearchOutlined />}
            style={{ width: 280 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
          />
          <Select
            value={typeFilter}
            onChange={setTypeFilter}
            style={{ width: 140 }}
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'WIRE_IN', label: 'Wire In' },
              { value: 'WIRE_OUT', label: 'Wire Out' },
              { value: 'CHECK_IN', label: 'Check In' },
              { value: 'CHECK_OUT', label: 'Check Out' },
              { value: 'ACH_IN', label: 'ACH In' },
              { value: 'ACH_OUT', label: 'ACH Out' },
            ]}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'UNRECONCILED', label: 'Unreconciled' },
              { value: 'PARTIAL', label: 'Partial' },
              { value: 'RECONCILED', label: 'Reconciled' },
              { value: 'PUSHED', label: 'Pushed to Xero' },
            ]}
          />
          <Select
            value={directionFilter}
            onChange={setDirectionFilter}
            style={{ width: 120 }}
            options={[
              { value: 'all', label: 'All' },
              { value: 'IN', label: 'Incoming' },
              { value: 'OUT', label: 'Outgoing' },
            ]}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null)}
            format="YYYY-MM-DD"
          />
          {(typeFilter !== 'all' ||
            statusFilter !== 'all' ||
            dateRange ||
            searchText ||
            directionFilter !== 'all') && (
            <Button
              icon={<FilterOutlined />}
              onClick={() => {
                setTypeFilter('all');
                setStatusFilter('all');
                setDateRange(null);
                setSearchText('');
                setDirectionFilter('all');
              }}
            >
              Clear Filters
            </Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Refresh
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            showTotal: (total) => `${total} transactions`,
          }}
          size="middle"
          scroll={{ x: 1320 }}
        />
      </Card>

      <PotentialMatchesModal
        visible={showMatchesModal}
        transaction={matchingTransaction}
        onClose={() => {
          setShowMatchesModal(false);
          setMatchingTransaction(null);
        }}
        onConfirm={handleMatchConfirm}
      />
    </div>
  );
};

const PaymentsList: React.FC = () => {
  return (
    <div style={{ padding: spacing.xl }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: spacing.lg,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <BankOutlined style={{ marginRight: 8 }} />
          Payments
        </Title>
        <XeroConnectionStatus compact />
      </div>

      <XeroConnectionStatus />

      <Tabs
        defaultActiveKey="transactions"
        items={[
          {
            key: 'transactions',
            label: (
              <span>
                <DollarOutlined />
                Transactions
              </span>
            ),
            children: <BankTransactionsTab />,
          },
          {
            key: 'credits',
            label: (
              <span>
                <CreditCardOutlined />
                Credits
              </span>
            ),
            children: <CreditsTab />,
          },
          {
            key: 'unmatched-invoices',
            label: (
              <span>
                <FileTextOutlined />
                Unmatched Invoices
              </span>
            ),
            children: <UnmatchedInvoicesTab />,
          },
        ]}
      />
    </div>
  );
};

export default PaymentsList;
